import React from 'react';
import {editorSchema, generateUUID} from '../../../../../components/prosemirror-editor';
import {
    addReference,
    initializeReferenceMark,
} from '../../../../../components/prosemirror-editor/yMaps/referencesYMap';
import useImportedContentStore from '../../../../../stores/useImportedContentStore';
import useYDocStore from '../../../../../stores/useYDocStore';
import {checkReferenceImportStatus} from '../../../../../utils/api/referenceImport';

function groupReferencesByStatement(resolvedReferences) {
    const groupMap = new Map();

    for (const resolved of resolvedReferences) {
        if (!resolved.segments || resolved.segments.length === 0) {
            continue;
        }

        const groupKey = `${resolved.paragraphIndex}|${resolved.statementText}`;
        if (!groupMap.has(groupKey)) {
            groupMap.set(groupKey, []);
        }
        groupMap.get(groupKey).push(resolved);
    }

    return groupMap;
}

function findParagraphByIndex(doc, paragraphIndex) {
    let paraPos = null;
    let paraNode = null;
    let nodeIdx = 0;

    doc.descendants((node, pos) => {
        if (paraPos !== null) return false;

        if (node.type.name === 'table') return false;
        if (node.type.name === 'paragraph' || node.type.name === 'heading') {
            if (nodeIdx === paragraphIndex) {
                paraPos = pos;
                paraNode = node;
                return false;
            }
            nodeIdx++;
        }

        return true;
    });

    return {paraPos, paraNode};
}

function applyResolvedReferences(editorView, resolvedReferences) {
    const {ydoc, yReferenceMarks} = useYDocStore.getState();
    if (!ydoc || !yReferenceMarks || !editorView) return;

    const {state} = editorView;
    const refMark = state.schema.marks.reference;
    if (!refMark) return;

    const tr = state.tr;
    let markCount = 0;
    const groupedReferences = groupReferencesByStatement(resolvedReferences);

    for (const [, group] of groupedReferences) {
        const first = group[0];
        const {paraPos, paraNode} = findParagraphByIndex(state.doc, first.paragraphIndex);

        if (paraPos === null || !paraNode) continue;

        const paraText = paraNode.textContent;
        const statementText = first.statementText;
        const matchIdx = paraText.indexOf(statementText);

        if (matchIdx === -1) continue;

        const from = paraPos + 1 + matchIdx;
        const to = from + statementText.length;
        const uid = generateUUID();

        try {
            tr.addMark(from, to, refMark.create({uid}));
            markCount++;

            ydoc.transact(() => {
                initializeReferenceMark(ydoc, yReferenceMarks, uid);

                for (const resolved of group) {
                    const primarySeg = resolved.segments[0];
                    addReference(ydoc, yReferenceMarks, uid, {
                        docId: resolved.docId,
                        docUrl: resolved.url,
                        content: primarySeg.content,
                        segmentId: primarySeg.segmentId,
                        index: primarySeg.index,
                        offset: primarySeg.offset ?? 0,
                        start: {
                            segmentIndex: primarySeg.index,
                            segmentOffset: primarySeg.offset ?? 0,
                        },
                        end: {
                            segmentIndex: primarySeg.index,
                            segmentOffset: (primarySeg.offset ?? 0) + (primarySeg.content?.length ?? 0),
                        },
                    });
                }
            }, 'reference-import');
        } catch (error) {
            console.warn('[ImportReport] Failed to apply reference mark:', error);
        }
    }

    if (markCount > 0) {
        editorView.dispatch(tr);
        console.log(`[ImportReport] Injected ${markCount} reference marks`);
    }
}

export default function useImportedContentBridge({editorView, reportId, enabled = true}) {
    React.useEffect(() => {
        if (!enabled) return;
        if (!editorView) return;

        const {content, reportId: importReportId, clear} = useImportedContentStore.getState();
        if (!content || importReportId !== reportId) return;

        const timer = setTimeout(() => {
            try {
                const parsedDoc = editorSchema.nodeFromJSON(content);
                const {state} = editorView;
                const tr = state.tr.replaceWith(0, state.doc.content.size, parsedDoc.content);
                editorView.dispatch(tr);
            } catch (error) {
                console.error('[ImportReport] Failed to inject imported content', error);
            } finally {
                clear();
            }
        }, 600);

        return () => clearTimeout(timer);
    }, [enabled, editorView, reportId]);

    React.useEffect(() => {
        if (!enabled) return;
        const {referenceImportStatus, reportId: importReportId} = useImportedContentStore.getState();

        if (referenceImportStatus !== 'processing' || importReportId !== reportId) return;
        if (!editorView) return;

        let cancelled = false;
        let timeoutId = null;

        const poll = async (interval) => {
            if (cancelled) return;

            try {
                const result = await checkReferenceImportStatus(reportId);
                if (cancelled) return;

                if (result.status === 'done') {
                    useImportedContentStore.getState().setReferenceImportStatus('done');
                    if (result.references && result.references.length > 0) {
                        applyResolvedReferences(editorView, result.references);
                    }
                } else if (result.status === 'failed' || result.status === 'not_found') {
                    useImportedContentStore.getState().setReferenceImportStatus(result.status);
                } else {
                    const nextInterval = Math.min(interval * 1.5, 10_000);
                    timeoutId = setTimeout(() => poll(nextInterval), nextInterval);
                }
            } catch (error) {
                console.warn('[ImportReport] Reference import poll error:', error);
                if (!cancelled) {
                    const nextInterval = Math.min(interval * 2, 10_000);
                    timeoutId = setTimeout(() => poll(nextInterval), nextInterval);
                }
            }
        };

        timeoutId = setTimeout(() => poll(3_000), 1_500);

        return () => {
            cancelled = true;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [enabled, editorView, reportId]);
}

